import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';



import OrderSummary from './components/OrderSummary';
import PaymentMethodTabs from './components/PaymentMethodTabs';
import BillingAddressForm from './components/BillingAddressForm';
import SecurityBadges from './components/SecurityBadges';

const SecureCheckoutPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Mock plan data - in real app, this would come from route state or API
  useEffect(() => {
    const planFromState = location?.state?.plan;
    if (planFromState) {
      setSelectedPlan(planFromState);
      setBillingCycle(location?.state?.billingCycle || 'monthly');
    } else {
      // Default plan if none selected
      setSelectedPlan({
        id: 'professional',
        name: 'Professional',
        monthlyPrice: 149,
        annualPrice: 1430,
        features: [
          'Unlimited Job Postings',
          'Enhanced Candidate Visibility',
          'Advanced Analytics Dashboard',
          'Priority Listing',
          'Email Support (24h response)'
        ]
      });
    }
  }, [location]);

  const handleApplyPromo = () => {
    // Mock promo code validation
    if (promoCode?.toLowerCase() === 'save20') {
      setPromoApplied(true);
      setDiscount(20);
    } else if (promoCode?.toLowerCase() === 'first10') {
      setPromoApplied(true);
      setDiscount(10);
    } else {
      alert('Invalid promo code');
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(false);
    setDiscount(0);
  };

  const handleCompletePurchase = (paymentData) => {
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      // Navigate to confirmation page with transaction data
      navigate('/payment-confirmation-receipt', {
        state: {
          transaction: {
            id: `TXN${Date.now()}`,
            amount: calculateTotal(),
            plan: selectedPlan,
            billingCycle,
            paymentMethod: paymentData?.method,
            timestamp: new Date()?.toISOString(),
            discount
          }
        }
      });
    }, 2000);
  };

  const calculateSubtotal = () => {
    if (!selectedPlan) return 0;
    return billingCycle === 'monthly' ? selectedPlan?.monthlyPrice : selectedPlan?.annualPrice;
  };

  const calculateTax = () => {
    return Math.round(calculateSubtotal() * 0.18); // 18% GST
  };

  const calculateDiscountAmount = () => {
    return Math.round(calculateSubtotal() * (discount / 100));
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscountAmount() + calculateTax();
  };

  if (!selectedPlan) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading checkout...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 border-b border-border mb-8 -mx-6 -mt-8 px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate('/pricing-subscription-plans')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              <span>Back to Pricing</span>
            </button>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
              Secure Checkout
            </h1>
            <p className="text-muted-foreground">
              Complete your subscription purchase securely
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Payment Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Plan Summary Card */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-warm-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-heading font-bold text-foreground mb-1">
                      {selectedPlan?.name} Plan
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Billed {billingCycle === 'monthly' ? 'Monthly' : 'Annually'}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/pricing-subscription-plans')}
                    className="text-sm text-accent hover:text-accent/80 font-medium"
                  >
                    Change Plan
                  </button>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-heading font-bold text-foreground">
                    ${calculateSubtotal()}
                  </span>
                  <span className="text-muted-foreground">
                    /{billingCycle === 'monthly' ? 'month' : 'year'}
                  </span>
                </div>
              </div>

              {/* Payment Method Section */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-warm-sm">
                <h2 className="text-lg font-heading font-bold text-foreground mb-4">
                  Payment Method
                </h2>
                <PaymentMethodTabs
                  onPaymentSubmit={handleCompletePurchase}
                  isProcessing={isProcessing}
                />
              </div>

              {/* Billing Address Section */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-warm-sm">
                <h2 className="text-lg font-heading font-bold text-foreground mb-4">
                  Billing Address
                </h2>
                <BillingAddressForm />
              </div>

              {/* Security Badges */}
              <SecurityBadges />
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <OrderSummary
                  plan={selectedPlan}
                  billingCycle={billingCycle}
                  subtotal={calculateSubtotal()}
                  tax={calculateTax()}
                  discount={calculateDiscountAmount()}
                  total={calculateTotal()}
                  promoCode={promoCode}
                  promoApplied={promoApplied}
                  onPromoCodeChange={setPromoCode}
                  onApplyPromo={handleApplyPromo}
                  onRemovePromo={handleRemovePromo}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SecureCheckoutPayment;